import React, { useState, useCallback } from 'react';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/styles';

import { SmartlookEventInformation, trackWithSmartlook } from '../util/track-with-smartlook';

const useStyles = makeStyles(() => ({
  outlinedInput: {
    fontSize: '16px',
    padding: '10.5px 14px',
  },
  notchedOutline: {
    '& > legend': {
      fontSize: 'calc(16px * .75)',
    },
  },
  outlinedLabel: {
    fontSize: '16px',
    transform: 'translate(14px, 12px) scale(1)',
    '&.shrink': {
      transform: 'translate(14px, -5px) scale(.75)',
    },
  },
}));

type InputProps<T extends string> = {
  label?: string;
  value: T;
  options: Array<SelectOption<T>>;
  onChange: (value: T) => void;
  className?: string;
  fullWidth?: boolean;
  smartlookEvent?: SmartlookEventInformation;
  disabled?: boolean;
  required?: boolean;
  /**
   * Cypress selector for identifying buttons in end-to-end testing
   * sets the data-cy attribute on the button element
   * @see https://docs.cypress.io/guides/references/best-practices#Selecting-Elements
   */
  dataCy?: string;
};

export const UiSelect = <T extends string>({
  className,
  label,
  value,
  options,
  onChange,
  fullWidth,
  smartlookEvent,
  disabled,
  required,
  dataCy,
}: InputProps<T>) => {
  const classes = useStyles();
  const [minWidth, setMinWidth] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleChange = ({ target }: React.ChangeEvent<{ value: unknown }>) => {
    if (smartlookEvent) {
      trackWithSmartlook(smartlookEvent);
    }
    if (!target.value && required) {
      setError('Required');
    }
    if (target.value && required) {
      setError(null);
    }
    onChange(target.value as T);
  };

  // IF (06/05/20): There's an issue with material UI that when you use an outlined select, it
  // doesn't base its initial width off the label. So without setting the width, you end up with a
  // smushed looking component. You can even see them setting a minWidth in the material ui
  // documentation https://material-ui.com/components/selects/#SimpleSelect.tsx. To get around this,
  // I'm creating a placeholder div from the label that has 0 height so it's not visible. I use that
  // to determine the minWidth so we don't have to pass it in and it's standard across all filters.
  const placeholderRef = useCallback((node) => {
    if (node) {
      setMinWidth(node.clientWidth + 50);
    } else {
      setMinWidth(0);
    }
  }, []);

  return (
    <div
      className={`u-display-flex u-flexDirection-column u-alignItems-flexStart ${
        fullWidth ? 'u-width-100' : ''
      }`}
    >
      <div ref={placeholderRef} className="typo-title u-height-0 u-overflow-hidden">
        {label}
      </div>
      <FormControl
        style={{ minWidth }}
        className={className}
        variant="outlined"
        fullWidth={fullWidth}
      >
        <InputLabel
          classes={{ shrink: 'shrink', outlined: classes.outlinedLabel }}
          required={required}
          error={Boolean(error)}
        >
          {label}
        </InputLabel>
        <Select
          value={value}
          label={label}
          onChange={handleChange}
          input={
            <OutlinedInput label={label} classes={{ notchedOutline: classes.notchedOutline }} />
          }
          variant="outlined"
          classes={{ outlined: classes.outlinedInput }}
          disabled={disabled}
          required={required}
          data-cy={dataCy}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
